import * as React from 'react';
import { PiggyBank } from 'lucide-react';

import { cn } from 'lib/utils';

/**
 * El logo de la gestora/entidad de un plan: el cuadradito redondeado con borde y fondo de
 * tarjeta que acompaña al nombre del plan en `ui/PlanCard`, en el desplegable de selección de
 * plan del sidebar y en el top bar móvil.
 *
 * Genérico a propósito: no conoce ids de plan ni qué gestora lleva cada uno. Quien lo usa pasa
 * la imagen (`src`), y si el plan aún no tiene entidad (p. ej. un plan nuevo pendiente de
 * identidad) se pinta el `fallback`, por defecto una hucha. El mapa de plan a imagen es dato de
 * negocio y vive en la aplicación, no aquí.
 */
export interface PlanLogoProps {
	/** Imagen de la entidad. Sin ella se pinta `fallback`. */
	src?: string;
	/** Nombre del plan o de la entidad. */
	name: string;
	/**
	 * Por defecto el logo es decorativo (`alt=""`): casi siempre va pegado al nombre del plan,
	 * que ya lo dice, y repetirlo lo leería dos veces un lector de pantalla. Pasar `false` si va
	 * solo, para que se anuncie `name` (con imagen o sin ella).
	 */
	decorative?: boolean;
	/** sm=28px (cabecera del selector), md=36px (listas y cards, por defecto), lg=40px. */
	size?: 'sm' | 'md' | 'lg';
	/** Qué se pinta sin `src`. Por defecto, una hucha. */
	fallback?: React.ReactNode;
	className?: string;
}

const SIZE_CLASSES: Record<NonNullable<PlanLogoProps['size']>, string> = {
	sm: 'h-7 w-7',
	md: 'h-9 w-9',
	lg: 'h-10 w-10'
};

export const PlanLogo = ({
	src,
	name,
	decorative = true,
	size = 'md',
	fallback,
	className
}: PlanLogoProps) => (
	<span
		// Sin decorar, el nombre lo lleva el contenedor y no la imagen: así se anuncia también cuando
		// no hay `src` y se pinta la hucha (que es `aria-hidden`).
		role={decorative ? undefined : 'img'}
		aria-label={decorative ? undefined : name}
		className={cn(
			// bg-card y border-border, no bg-white ni border-gray-100: los dos temas ya los resuelven.
			'flex flex-none items-center justify-center overflow-hidden rounded-control border border-border bg-card',
			SIZE_CLASSES[size],
			className
		)}
	>
		{src ? (
			<img src={src} alt="" className="h-full w-full object-contain p-1" />
		) : (
			(fallback ?? <PiggyBank className="h-1/2 w-1/2 text-primary" aria-hidden="true" />)
		)}
	</span>
);
